import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    inject,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import {
    RouteEnum,
    RouteMapItem,
    ROUTEPERMISSIONS,
} from '@sharedEnums/route-permissions';
import { UserResponse } from '@userFeature/interfaces//api/user-response.interface';
import { UserService } from '@userFeature/services/api/user.service';
import { AlertService } from '@utils/alert-service/alert-service.service';
import { hideitemNavigation } from '@utils/hide-item-navigation/hide-item-navigation';
import { ImageValidatorService } from '@utils/image-validator-service/image-validator.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { LoggedInUser } from 'app/modules/auth/interfaces/frontend/logged-in-user.interface';
import { AuthService } from 'app/modules/auth/services/frontend/auth.service';
import { environment } from 'environments/environment';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        FuseFullscreenComponent,
        RouterOutlet,
        MatIconModule,
        MatIconButton,
        CommonModule,
        MatMenuModule,
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private fuseConfigService = inject(FuseConfigService);
    private alertService = inject(AlertService);
    private confirmationService = inject(FuseConfirmationService);
    private userService = inject(UserService);
    private imageValidatorService = inject(ImageValidatorService);
    private authService = inject(AuthService);

    loggedInUser = computed(() => this.authService.loggedInUserSignal());

    manualCRMConnect = environment.manualCRMConnect;
    manualCareerPortal = environment.manualCareerPortal;
    manualCMS = environment.manualCMS;

    config: FuseConfig;
    scheme: 'dark' | 'light';
    isScreenSmall: boolean;
    navigation: Navigation;
    user: LoggedInUser;

    /**
     * Constructor
     */
    constructor(
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._authService.loggedInUser$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user) => {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('lg');
            });

        this.setConfig();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    setScheme(scheme: any): void {
        this.fuseConfigService.config = { scheme };
        this.scheme = scheme;
        this.setConfig();
    }

    setConfig(): void {
        const configLayout = localStorage.getItem('fuse-config');

        if (configLayout) {
            const parsedConfig = JSON.parse(configLayout);

            this.scheme = parsedConfig.scheme;
        }
    }

    async onFileSelected(event: any) {
        const formData = new FormData();
        const file: File = event.target.files[0];

        if (!this.imageValidatorService.isImage(file.type)) {
            this.alertService.errorAlert(
                [],
                'Tipo de archivo inválido. Debe ingresar una imagen.'
            );

            return;
        }

        const dialog = this.confirmationService.open({
            title: 'Cambiar foto',
            message: `¿Esta seguro de cambiar la foto de perfil?`,
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation-triangle',
                color: 'primary',
            },
            actions: {
                confirm: {
                    label: 'Cambiar',
                    color: 'primary',
                },
                cancel: {
                    label: 'Cancelar',
                },
            },
        });

        const confirmationResult = await lastValueFrom(dialog.afterClosed());
        if (confirmationResult != 'confirmed') return;

        if (file) {
            formData.append('ProfilePicture', file);
        }

        this.updateProfilePicture(formData);
    }

    updateProfilePicture(profilePicture: FormData) {
        this.userService
            .updateProfilePicture(profilePicture)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: async (response) => {
                    this.setProfilePicture(response.data);

                    this.alertService.saveAlert(response.message);

                    this._authService.signInUsingToken();
                },
                error: (err) => {
                    this.alertService.handleAlertResponse(err);
                },
            });
    }

    async removeProfilePicture() {
        const dialog = this.confirmationService.open({
            title: 'Remover foto',
            message: `¿Esta seguro de quitar la foto de perfil?`,
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation-triangle',
                color: 'warn',
            },
            actions: {
                confirm: {
                    label: 'Remover',
                    color: 'warn',
                },
                cancel: {
                    label: 'Cancelar',
                },
            },
        });

        const confirmationResult = await lastValueFrom(dialog.afterClosed());
        if (confirmationResult != 'confirmed') return;

        this.userService
            .updateProfilePicture()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: async (response) => {
                    this.setProfilePicture(response.data);

                    this.alertService.saveAlert(response.message);

                    this._authService.signInUsingToken();
                },
                error: (err) => {
                    this.alertService.handleAlertResponse(err);
                },
            });
    }

    setProfilePicture(userResponse: UserResponse) {
        this.user.profilePicture = userResponse.profilePicture;

        localStorage.removeItem('user');
        localStorage.setItem('user', JSON.stringify(this.user));
    }

    getRouteModuleBoolean(route: RouteMapItem): boolean {
        return !hideitemNavigation({
            permissions: route.permissions,
            user: this.loggedInUser(),
            mode: route.mode,
        });
    }

    get canViewCRMConnect(): boolean {
        const routePermissionObject = ROUTEPERMISSIONS[RouteEnum.CRM_CONNECT];
        return this.getRouteModuleBoolean(routePermissionObject);
    }

    get canViewCareerPortal(): boolean {
        const routePermissionObject = ROUTEPERMISSIONS[RouteEnum.CAREER_PORTAL];
        return this.getRouteModuleBoolean(routePermissionObject);
    }

    get canViewCMS(): boolean {
        const routePermissionObject = ROUTEPERMISSIONS[RouteEnum.CMS];
        return this.getRouteModuleBoolean(routePermissionObject);
    }
}
